import React, { useState, useEffect, useRef } from "react";
import { Button, Input, InputGroup, InputGroupText } from "reactstrap";
import "./WavingHand.css";
import {
  FaHandPaper,
  FaTimes,
  FaPaperPlane,
  FaEraser,
  FaTrashAlt,
} from "react-icons/fa";
import io from "socket.io-client";
import axios from "axios";
import { useBulkDelete } from "../utils/useBulkDelete";
import FilePreviewModal from "./FilePreviewModal";
import { fetchSignedUrl } from "../utils/fetchSignedUrl";
import AnimatedIcons from "../Home/AnimatedIcons";
import { useSelector } from "react-redux";

const socket = io(`${process.env.REACT_APP_NAADAN_API}`, {
  autoConnect: true,
  reconnection: true, // <-- allow auto reconnect
  reconnectionAttempts: 5, // <-- try reconnecting 5 times
  reconnectionDelay: 1000, // <-- wait 1s before retry
});

const ChatWidget = ({ clear }) => {
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;
  const { branch, token } = userData;
  const userId = userData?.customer?.mobileNo || "guest";
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const messagesEndRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  // fileInputRef for image select
  // handleFileSelect also
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file)); // temporary preview
      setShowModal(true);
    }
  };

  const { deleteDocuments, results, loading, error } = useBulkDelete();

  const toggleChat = () => setIsOpen(!isOpen);
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const isGuestMessage =
    lastMessage?.message === "Welcome guest! Please login to continue.";

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const res = await deleteDocuments({
      collectionName: "chats",
      branch: userData.branch,
      ids: selectedIds,
      token: userData.token,
    });
    // Optionally, you can remove deleted messages from local state

    if (res) {
      // ✅ Only refresh after successful deletion
      fetchHistory();
      setSelectedIds([]);
    }
  };

  const handleClearSelection = () => setSelectedIds([]);

  // Scroll to bottom on new message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Join branch and fetch history
  // const fetchHistory = async () => {
  //   try {
  //     const res = await axios.get(
  //       `${process.env.REACT_APP_COLLEGE_ERP_API}chat-messages/${branch}/${userId}`,
  //       { headers: { Authorization: `Bearer ${token}` } }
  //     );
  //     setMessages(res.data || []);
  //   } catch (err) {
  //     console.error("Error fetching chat history:", err);
  //   }
  // };
  const fetchHistory = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_NAADAN_API}chat-messages/${branch}/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = res.data || [];

      // ✅ Resolve fileKeys into signed URLs
      const withUrls = await Promise.all(
        data.map(async (msg) => {
          if (msg.fileKey) {
            const url = await fetchSignedUrl(msg.fileKey);
            return { ...msg, fileUrl: url };
          }
          return msg;
        })
      );

      setMessages(withUrls);
    } catch (err) {
      console.error("Error fetching chat history:", err);
    }
  };

  useEffect(() => {
    //  if (!branch || !userId) return;
    socket.emit("joinBranch", { branch, role: "user", senderId: userId });
    if (branch && userId && token) {
      fetchHistory();
    }

    const handleNewMessage = async (msg) => {
      if (
        msg.senderId === userId ||
        msg.receiverId === userId ||
        msg.senderId === "guest" ||
        msg.receiverId === "system"
      ) {
        if (msg.fileKey) {
          const url = await fetchSignedUrl(msg.fileKey);
          setMessages((prev) => [...prev, { ...msg, fileUrl: url }]);
          return;
        }
        setMessages((prev) => [...prev, msg]);
      }
    };

    // 👇 reconnect handling
    socket.on("connect", () => {
      console.log("Socket connected again ✅");
      socket.emit("joinBranch", { branch, role: "user", senderId: userId });
      fetchHistory(); // refresh old + missed messages
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected ❌");
    });

    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.off("connect");
      socket.off("disconnect");
    };
  }, [branch, userId, token]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    // If branch, userId, or token missing → auto route to system
    if (!branch || !token) {
      const msg = {
        branch: branch || "unknown",
        senderId: userId || "guest",
        senderRole: "guest",
        receiverId: "system",
        receiverRole: "system",
        message: newMessage.trim(),
        createdAt: new Date(),
      };

      socket.emit("sendMessage", msg);
      return;
    }

    const msg = {
      branch,
      senderId: userId,
      senderRole: "user",
      receiverId: "support",
      receiverRole: "support",
      message: newMessage.trim(),
      createdAt: new Date(),
    };
    console.log(msg);
    socket.emit("sendMessage", msg);

    try {
      await axios.post(`${process.env.REACT_APP_NAADAN_API}send`, msg, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Error sending message:", err);
    }

    setNewMessage("");
  };

  const handleUploadSuccess = async (fileKey) => {
    try {
      const url = await fetchSignedUrl(fileKey);

      const newMessage = {
        id: Date.now(),
        type: "file",
        fileKey,
        message: "img",
        fileUrl: url, // ✅ add URL immediately
        createdAt: new Date(),
        senderRole: "user",
        senderId: userId,
      };

      //  setMessages((prev) => [...prev, newMessage]);
      const msg = {
        branch,
        senderId: userId,
        senderRole: "user",
        receiverId: "support",
        receiverRole: "support",
        message: "",
        fileKey,
        fileUrl: url, // ✅ add URL immediately
        createdAt: new Date(),
      };

      socket.emit("sendMessage", msg);

      try {
        await axios.post(`${process.env.REACT_APP_NAADAN_API}send`, msg, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.error("Error sending message:", err);
      }

      setNewMessage("");
    } catch (err) {
      console.error("Error resolving file URL:", err);
    }
  };

  return (
    <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}>
      {!isOpen && (
        <div className="chat-icon" onClick={toggleChat}>
          {/* <FaHandPaper className="hand" /> */}
          <span className="hand"> 👋 </span>
          {/* <AnimatedIcons onClick={() => alert("Welcome to Naadan Mart!")} /> */}
        </div>
      )}

      {isOpen && (
        <div
          style={{
            width: 320,
            height: 400,
            display: "flex",
            flexDirection: "column",
            border: "1px solid #ccc",
            borderRadius: 12,
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: 10,
              background: "#4e73df",
              color: "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>💬 Support Chat</span>
            <FaTimes style={{ cursor: "pointer" }} onClick={toggleChat} />
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              padding: 10,
              overflowY: "auto",
              background: "#fafafa",
            }}
          >
            {messages.length === 0 ? (
              <p style={{ textAlign: "center", color: "#888" }}>
                👋 Say hello to support!
              </p>
            ) : (
              messages.map((msg, i) => {
                const date = new Date(msg.createdAt);
                const formattedDate = date.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "2-digit",
                });
                const formattedTime = date.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                const timeWithDate = `${formattedDate} ${formattedTime}`;
                const isUser =
                  msg.senderRole === "user" || msg.senderRole === "guest";

                const isSelected = selectedIds.includes(msg._id);

                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: isUser ? "flex-end" : "flex-start",
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    {isUser && (
                      <span
                        style={{ fontSize: 10, color: "#888", marginRight: 4 }}
                      >
                        {timeWithDate}
                      </span>
                    )}

                    <div
                      style={{
                        padding: "6px 10px",
                        borderRadius: 12,
                        background: msg.fileUrl
                          ? "transparent"
                          : isUser
                          ? "#4e73df"
                          : "#e2e6ea",

                        //  isUser && !msg.fileUrl ? "#4e73df" : "#e2e6ea",
                        color: isUser ? "#fff" : "#000",
                        maxWidth: "70%",
                        border: isSelected ? "2px solid red" : "none",
                        position: "relative",
                        cursor: "pointer",
                      }}
                      onClick={() => toggleSelect(msg._id)}
                    >
                      {/* ✅ Text message */}
                      {msg.message && <div>{msg.message}</div>}

                      {/* ✅ File (image or link) */}
                      {msg.fileUrl &&
                        (msg.fileKey.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img
                            src={msg.fileUrl}
                            alt="uploaded"
                            style={{
                              maxWidth: "200px",
                              borderRadius: "8px",
                              marginTop: "5px",
                            }}
                          />
                        ) : (
                          <a
                            href={msg.fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📎 Download File
                          </a>
                        ))}

                      {isSelected && (
                        <FaTrashAlt
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            cursor: "pointer",
                            color: "red",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteDocuments({
                              collectionName: "chats",
                              branch,
                              ids: [msg._id],
                              token,
                            });
                            setSelectedIds((prev) =>
                              prev.filter((x) => x !== msg._id)
                            );
                          }}
                        />
                      )}
                    </div>

                    {!isUser && (
                      <span
                        style={{ fontSize: 10, color: "#888", marginLeft: 4 }}
                      >
                        {timeWithDate}
                      </span>
                    )}
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Selected Actions */}
          {selectedIds.length > 0 && (
            <div
              style={{
                padding: 5,
                borderTop: "1px solid #ddd",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button size="sm" color="danger" onClick={handleDeleteSelected}>
                <FaTrashAlt /> Delete Selected
              </Button>
              <Button
                size="sm"
                color="secondary"
                onClick={handleClearSelection}
              >
                <FaEraser /> Clear Selection
              </Button>
            </div>
          )}

          {/* Input */}
          <div style={{ padding: 10, borderTop: "1px solid #ddd" }}>
            <InputGroup>
              <>
                <InputGroupText
                  style={{ cursor: "pointer", background: "#e2e6ea" }}
                  onClick={() => fileInputRef.current.click()} // trigger hidden input
                >
                  📎
                </InputGroupText>

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  disabled={!branch || isGuestMessage}
                />
              </>
              <Input
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                disabled={isGuestMessage}
              />
              <InputGroupText
                style={{
                  cursor: "pointer",
                  background: "#4e73df",
                  color: "#fff",
                  border: "none",
                }}
                onClick={handleSend}
                disabled={isGuestMessage && !userData.token}
              >
                <FaPaperPlane />
              </InputGroupText>
            </InputGroup>
          </div>
        </div>
      )}

      {/* File preview modal */}
      <FilePreviewModal
        showModal={showModal}
        setShowModal={setShowModal}
        previewUrl={previewUrl}
        setPreviewUrl={setPreviewUrl}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        folderName="chat_attachments"
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default ChatWidget;
